import { ReactFlowProvider } from '@xyflow/react';
import React, { lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import type { TabOption } from '@/components/chariot-ui';
import { Tabs } from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { FeatureLock } from '@/components/ui/FeatureLock';
import { PraetorianBadge } from '@/components/ui/PraetorianBadge';
import { useAuth } from '@/state/auth';

import type { AttackTabOption } from './constants';
import { AttacksTab, attacksTabs } from './constants';

// Lazy load tabs for code splitting
const AttackPaths = lazy(() => import('./attackPaths'));
const Capabilities = lazy(() => import('./capabilities'));
const TemplateRefinement = lazy(() => import('./templateRefinement'));
const RedTeam = lazy(() => import('./redTeam'));
const Osint = lazy(() => import('./osint'));

/**
 * Loading fallback component for lazy-loaded tabs.
 */
function TabLoadingFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader />
    </div>
  );
}

/**
 * Attacks section main container.
 *
 * Manages four tabs:
 * - Attack Paths: Visualize attack path graphs
 * - Capabilities: Browse and run security capabilities (Praetorian only)
 * - Template Refinement: Refine Nuclei templates with AI assistance (Praetorian only)
 * - Red Team: Manage red team operations (Praetorian only)
 *
 * URL state is synced via `?tab=` query parameter.
 */
export function Attacks() {
  const { isPraetorianUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Show all tabs to all users (locked tabs show feature locked screen)
  const visibleTabs = attacksTabs;

  const validTabValues = useMemo(
    () => visibleTabs.map(tab => tab.value),
    [visibleTabs]
  );

  // Red Team sub-tabs that should map to the RED_TEAM tab
  const redTeamSubTabs = useMemo(
    () => ['gcp-deployment', 'parallel-parking', 'nexus'],
    []
  );

  const defaultTab = AttacksTab.ATTACK_PATHS;

  // Derive selected tab directly from URL (single source of truth)
  const selectedTabValue = useMemo(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      // Check if it's a Red Team sub-tab
      if (redTeamSubTabs.includes(tabFromUrl)) {
        return AttacksTab.RED_TEAM;
      }
      // Check if it's a valid top-level tab
      if (validTabValues.includes(tabFromUrl)) {
        return tabFromUrl;
      }
    }
    return defaultTab;
  }, [searchParams, validTabValues, redTeamSubTabs]);

  // Check if the currently selected tab is locked for this user
  const isSelectedTabLocked = useMemo(() => {
    const tab = attacksTabs.find(
      t => t.value === selectedTabValue
    ) as AttackTabOption;
    return tab?.requiresPraetorian && !isPraetorianUser;
  }, [selectedTabValue, isPraetorianUser]);

  // Update URL when tab is selected
  const setSelectedTab = (newTab: string) => {
    if (validTabValues.includes(newTab)) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', newTab);
          return newParams;
        },
        { replace: true }
      );
    }
  };

  const selectedTab: TabOption | null =
    visibleTabs.find(tab => tab.value === selectedTabValue) || null;

  const handleTabSelect = (tab: TabOption) => {
    setSelectedTab(tab.value as string);
  };

  const renderContent = () => {
    switch (selectedTabValue) {
      case AttacksTab.ATTACK_PATHS:
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <ReactFlowProvider>
              <AttackPaths />
            </ReactFlowProvider>
          </Suspense>
        );
      case AttacksTab.CAPABILITIES:
        if (isSelectedTabLocked) {
          return (
            <div className="relative flex-1">
              <FeatureLock
                locked={true}
                title="Feature Locked"
                description={
                  <div className="flex flex-col items-center gap-2">
                    <PraetorianBadge />
                    <span>
                      This area is restricted to Praetorian users only
                    </span>
                  </div>
                }
              >
                <div className="size-full" />
              </FeatureLock>
            </div>
          );
        }
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <Capabilities />
          </Suspense>
        );
      case AttacksTab.TEMPLATE_REFINEMENT:
        if (isSelectedTabLocked) {
          return (
            <div className="relative flex-1">
              <FeatureLock
                locked={true}
                title="Feature Locked"
                description={
                  <div className="flex flex-col items-center gap-2">
                    <PraetorianBadge />
                    <span>
                      This area is restricted to Praetorian users only
                    </span>
                  </div>
                }
              >
                <div className="size-full" />
              </FeatureLock>
            </div>
          );
        }
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <TemplateRefinement />
          </Suspense>
        );
      case AttacksTab.RED_TEAM:
        if (isSelectedTabLocked) {
          return (
            <div className="relative flex-1">
              <FeatureLock
                locked={true}
                title="Feature Locked"
                description={
                  <div className="flex flex-col items-center gap-2">
                    <PraetorianBadge />
                    <span>
                      This area is restricted to Praetorian users only
                    </span>
                  </div>
                }
              >
                <div className="size-full" />
              </FeatureLock>
            </div>
          );
        }
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <RedTeam />
          </Suspense>
        );
      case AttacksTab.OSINT:
        if (isSelectedTabLocked) {
          return (
            <div className="relative flex-1">
              <FeatureLock
                locked={true}
                title="Feature Locked"
                description={
                  <div className="flex flex-col items-center gap-2">
                    <PraetorianBadge />
                    <span>
                      This area is restricted to Praetorian users only
                    </span>
                  </div>
                }
              >
                <div className="size-full" />
              </FeatureLock>
            </div>
          );
        }
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <Osint />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <ReactFlowProvider>
              <AttackPaths />
            </ReactFlowProvider>
          </Suspense>
        );
    }
  };

  return (
    <AppRouteBody title="Attacks">
      <div className="flex h-full flex-col overflow-hidden">
        <Tabs
          tabs={visibleTabs}
          selected={selectedTab}
          onSelect={handleTabSelect}
          variant="primary"
        />
        <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </AppRouteBody>
  );
}
